import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h2, sub, input, label, sectionLabel, btn, pill, RGB, STATUS_COLOR, Icon, ICONS, ConfirmButton, WeaponIcon, errMsg, ratingOf, ratingColor, RatingLabel } from './extUi';
import ExtMatchFeed, { FEED_TYPES } from './ExtMatchFeed';
import ExtPlayerStats, { FLUX3_COLS } from './ExtPlayerStats';
import ExtVetoSummary from './ExtVetoSummary';
import ExtMapLogs from './ExtMapLogs';

const SIDE = { CT: { color: '#5b9cf5', rgb: '91,156,245' }, T: { color: '#e8a33d', rgb: '232,163,61' } };
const REASON_ICON = { elimination: null, bomb_exploded: '/killfeed/explosion.svg', bomb_defused: '/killfeed/defuse.svg', time: '/killfeed/stopwatch.svg' };

function hpColor(hp) {
  if (hp >= 70) return 'var(--accent-green)';
  if (hp >= 40) return 'var(--accent-blue)';
  if (hp >= 20) return 'var(--accent-amber)';
  return 'var(--accent-red)';
}

// TeamTable + RoundStrip are also the panel match's Live view (components/match/MatchLiveView.js)
export function TeamTable({ team, players, side, roundLive }) {
  const { t } = useTranslation();
  const sc = side ? SIDE[side] : null;
  const th = { ...sectionLabel, padding: '0.45rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden', borderColor: sc ? `rgba(${sc.rgb},0.25)` : 'var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 1rem', borderBottom: '1px solid var(--border-subtle)', background: sc ? `rgba(${sc.rgb},0.06)` : 'transparent' }}>
        {side && <span style={{ ...pill(sc.rgb), fontFamily: 'var(--font-mono, monospace)' }}>{side}</span>}
        <span style={{ fontWeight: 800, fontFamily: 'var(--font-display, sans-serif)', fontSize: '1rem', color: 'var(--text-primary)' }}>{team.name}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: '1rem' }}>{t('ext.sb.player', 'Player')}</th>
              <th style={{ ...th, textAlign: 'left', width: 130 }}>{t('ext.sb.hp', 'HP')}</th>
              <th style={{ ...th, textAlign: 'left' }}>{t('ext.sb.equipment', 'Equipment')}</th>
              <th style={th}>{t('ext.sb.money', '$')}</th>
              <th style={th}>K</th><th style={th}>D</th><th style={th}>A</th><th style={th}>+/-</th>
              <th style={th}>ADR</th><th style={th}>HS%</th><th style={th}>KAST</th><th style={{ ...th, paddingRight: '1rem' }}
                title={t('ext.stat.ratingHltv', 'HLTV Rating 2.0 (estimate, same formula on every match)')}><RatingLabel /></th>
            </tr>
          </thead>
          <tbody>
            {players.length === 0 && <tr><td colSpan={12} style={{ padding: '0.8rem 1rem', color: 'var(--text-muted)' }}>{t('ext.sb.noPlayers', 'No players seen yet.')}</td></tr>}
            {players.map(p => {
              const dead = !p.alive; // server keeps end-of-round deaths until the next round starts
              const td = { padding: '0.45rem 0.5rem', borderTop: '1px solid var(--border-subtle)', textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
              return (
                <tr key={p.steam_id} style={{ opacity: dead || !p.connected ? 0.42 : 1, transition: 'opacity 0.3s' }}>
                  <td style={{ ...td, textAlign: 'left', paddingLeft: '1rem', fontFamily: 'inherit', color: 'var(--text-primary)', fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      {p.name}
                      {p.has_bomb && <img src="/killfeed/bombplant.png" alt="C4" title="C4" style={{ height: 13 }} />}
                      {p.defuse_kit && <img src="/killfeed/defuse.svg" alt={t('ext.sb.kit', 'Defuse kit')} title={t('ext.sb.kit', 'Defuse kit')} style={{ height: 13 }} />}
                      {!p.connected && <span style={pill(RGB.red)}>{t('ext.sb.disconnected', 'DC')}</span>}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', width: 120 }}>
                      <span style={{ width: 26, textAlign: 'right', color: dead ? 'var(--text-muted)' : hpColor(p.hp), fontWeight: 700 }}>{dead ? 0 : p.hp}</span>
                      <span style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${dead ? 0 : Math.max(0, Math.min(100, p.hp))}%`, background: hpColor(p.hp), transition: 'width 0.25s' }} />
                      </span>
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      {p.armor > 0 && <span style={pill(RGB.gray)} title={`${t('ext.sb.armor', 'Armor')} ${p.armor}`}>{p.helmet ? t('ext.sb.helmet', 'K+H') : t('ext.sb.kevlar', 'K')}</span>}
                      <WeaponIcon name={p.primary} height={15} />
                      <WeaponIcon name={p.secondary} height={13} />
                      {p.grenades.map((g, i) => <WeaponIcon key={g + i} name={g} height={13} />)}
                    </span>
                  </td>
                  <td style={{ ...td, color: 'var(--accent-green)' }}>{p.money != null ? `$${p.money}` : '—'}</td>
                  <td style={{ ...td, color: 'var(--text-primary)' }}>{p.kills}</td>
                  <td style={td}>{p.deaths}</td>
                  <td style={td}>{p.assists}</td>
                  <td style={{ ...td, color: p.kd_diff > 0 ? 'var(--accent-green)' : p.kd_diff < 0 ? 'var(--accent-red)' : td.color }}>{p.kd_diff > 0 ? `+${p.kd_diff}` : p.kd_diff}</td>
                  <td style={td}>{p.adr}</td>
                  <td style={td}>{p.hs_pct}%</td>
                  <td style={td}>{p.kast_pct}%</td>
                  <td style={{ ...td, paddingRight: '1rem', color: ratingColor(ratingOf(p)) || 'var(--text-primary)', fontWeight: 700 }}>{ratingOf(p).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RoundStrip({ rounds, mapIndex }) {
  const { t } = useTranslation();
  const list = rounds.filter(r => r.map_index === mapIndex);
  if (!list.length) return null;
  return (
    <div style={card}>
      <div style={{ ...sectionLabel, marginBottom: '0.55rem' }}>{t('ext.roundHistory', 'Round history')}</div>
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {list.map(r => {
          const side = r.winner_side ? SIDE[r.winner_side] : null;
          const icon = REASON_ICON[r.reason] || (r.winner_side === 'CT' ? '/killfeed/skull_t.svg' : '/killfeed/skull_ct.svg');
          return (
            <div key={r.round} title={`${t('ext.round', 'Round')} ${r.round} · ${r.reason} · ${r.score.team1}-${r.score.team2}`}
              style={{ width: 30, padding: '0.25rem 0', textAlign: 'center', borderRadius: 'var(--radius-sm)', background: side ? `rgba(${side.rgb},0.12)` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${side ? `rgba(${side.rgb},0.3)` : 'var(--border-subtle)'}`, marginLeft: r.round === 13 || (r.round > 24 && (r.round - 25) % 3 === 0) ? 8 : 0 }}>
              <img src={icon} alt={r.reason} style={{ height: 12, opacity: 0.85 }} />
              <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)' }}>{r.round}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RosterEditor({ match, onSaved }) {
  const { t } = useTranslation();
  const toText = (list) => (list || []).filter(p => p.origin === 'admin').map(p => `${p.steam_id}${p.name ? ' ' + p.name : ''}`).join('\n');
  const [r1, setR1] = useState(toText(match.roster.team1));
  const [r2, setR2] = useState(toText(match.roster.team2));
  const [busy, setBusy] = useState(false);
  const parse = (txt) => txt.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => { const [s, ...n] = l.split(/[\s,;]+/); return { steam_id: s, name: n.join(' ') || null }; });
  const learnt = (list) => (list || []).filter(p => p.origin === 'learnt');
  return (
    <div style={card}>
      <h2 style={h2}>{t('ext.roster', 'Roster')}</h2>
      <p style={sub}>{t('ext.rosterHint', 'Steam IDs make matching and team assignment exact. Players seen in the logs are added automatically (learnt). Saving re-parses the match.')}</p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {[[1, r1, setR1], [2, r2, setR2]].map(([n, val, setVal]) => (
          <div key={n} style={{ flex: '1 1 280px' }}>
            <label style={label}>{match.teams[`team${n}`].name}</label>
            <textarea style={{ ...input, minHeight: 100, fontFamily: 'var(--font-mono, monospace)', fontSize: '0.76rem' }} value={val} onChange={e => setVal(e.target.value)} placeholder="76561198000000001 Player1" />
            {learnt(match.roster[`team${n}`]).length > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                {t('ext.learnt', 'Learnt from logs')}: {learnt(match.roster[`team${n}`]).map(p => p.name || p.steam_id).join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: '0.6rem' }}>
        <button type="button" disabled={busy} style={btn(RGB.green, true)} onClick={async () => { setBusy(true); try { await axios.put(`/api/v1/ext/tournaments/${match.tournament_id}/matches/${match.id}/roster`, { team1: parse(r1), team2: parse(r2) }); onSaved(); } finally { setBusy(false); } }}>
          {busy ? t('common.loading', 'Loading...') : t('ext.saveRoster', 'Save roster')}
        </button>
      </div>
    </div>
  );
}

export default function ExtMatchView() {
  const { t } = useTranslation();
  const { id: tid, mid } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [sb, setSb] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [events, setEvents] = useState([]);
  const [conn, setConn] = useState('connecting');
  const [error, setError] = useState('');
  const [showRoster, setShowRoster] = useState(false);
  const wsRef = useRef(null);

  const loadRest = useCallback(async () => {
    try {
      const [m, s, r] = await Promise.all([
        axios.get(`/api/v1/ext/tournaments/${tid}/matches/${mid}`),
        axios.get(`/api/v1/ext/tournaments/${tid}/matches/${mid}/scoreboard`),
        axios.get(`/api/v1/ext/tournaments/${tid}/matches/${mid}/rounds`),
      ]);
      // The whole match, A to Z (paged)
      const all = [];
      let since = 0;
      for (let i = 0; i < 50; i++) {
        const e = await axios.get(`/api/v1/ext/tournaments/${tid}/matches/${mid}/events`, { params: { types: FEED_TYPES.join(','), limit: 2000, since } });
        all.push(...(e.data.data || []));
        since = e.data.last_id;
        if (!e.data.has_more) break;
      }
      setMatch(m.data); setSb(s.data); setRounds(r.data.data || []); setEvents(all);
      setError('');
    } catch (err) { setError(errMsg(err, t('ext.loadFailed', 'Failed to load'))); }
  }, [mid, t]);

  useEffect(() => { loadRest(); }, [loadRest]);

  // Live: WebSocket (snapshot → scoreboard ticks + events); reconnects with backoff
  useEffect(() => {
    let closed = false, retry = 0, timer = null;
    const connect = () => {
      const token = localStorage.getItem('token');
      const ws = new WebSocket(`${window.location.origin.replace(/^http/, 'ws')}/ws/ext?token=${encodeURIComponent(token || '')}&tournament=${tid}&match=${mid}`);
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; setConn('live'); };
      ws.onmessage = (msg) => {
        let m; try { m = JSON.parse(msg.data); } catch (_) { return; }
        if (m.type === 'snapshot') { setMatch(prev => ({ ...(prev || {}), ...m.match })); if (m.scoreboard) setSb(m.scoreboard); }
        else if (m.type === 'scoreboard') { setSb(m.scoreboard); setMatch(prev => prev ? { ...prev, ...m.state } : m.state); }
        else if (m.type === 'event') {
          // Restores / map resets void earlier events — reload everything; otherwise append
          if (m.event.type === 'round_restored' || m.event.type === 'map_reset') { loadRest(); return; }
          if (FEED_TYPES.includes(m.event.type)) setEvents(prev => (prev.some(x => x.id === m.event.id) ? prev : [...prev, m.event]));
          if (m.event.type === 'round_end') axios.get(`/api/v1/ext/tournaments/${tid}/matches/${mid}/rounds`).then(r => setRounds(r.data.data || [])).catch(() => { });
        } else if (m.type === 'match_rebuilt') { loadRest(); }
        else if (m.type === 'seating_update') { setMatch(prev => (prev ? { ...prev, seating: m.seating } : prev)); }
      };
      ws.onclose = () => {
        setConn('reconnecting');
        if (closed) return;
        retry = Math.min(retry + 1, 6);
        timer = setTimeout(connect, 500 * 2 ** retry);
      };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); if (wsRef.current) wsRef.current.close(); };
  }, [tid, mid, loadRest]);

  if (!match) return <div style={sub}>{error || t('common.loading', 'Loading...')}</div>;

  const s1 = match.teams.team1, s2 = match.teams.team2;
  const side1 = (sb && sb.sides && sb.sides.team1) || s1.side;
  const side2 = (sb && sb.sides && sb.sides.team2) || s2.side;
  const score = (sb && sb.score) || { team1: s1.score, team2: s2.score };
  const mapIndex = sb ? sb.map_index : match.current_map_index;
  const phaseColor = STATUS_COLOR[match.phase] || RGB.gray;
  const bigScore = (val, side) => <span style={{ fontFamily: 'var(--font-display, sans-serif)', fontWeight: 800, fontSize: '2.2rem', color: side ? SIDE[side].color : 'var(--text-primary)', minWidth: 48, textAlign: 'center' }}>{val}</span>;

  return (
    <div>
      <div style={{ marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Link to={`/admin/tournaments/${tid}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('ext.backToTournament', 'Tournament')}</Link>
        {match.seating && (
          <Link to={`/admin/tournaments/${tid}/matches/${mid}/seating`} title={t('extSeating.editSeatingHint', 'Change the stage seating of this match only')}
            style={{ ...btn(RGB.cyan), textDecoration: 'none', marginLeft: 'auto' }}>{t('extSeating.editSeating', 'Edit seating')}</Link>
        )}
      </div>

      {/* Score header */}
      <div style={{ ...card, padding: '1.1rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <span style={pill(phaseColor)}>{t(`ext.phase.${match.phase}`, match.phase)}</span>
          {match.current_round && <span style={pill(RGB.gray)}>{t('ext.roundN', 'Round {{n}}', { n: match.current_round })}</span>}
          {match.pause && <span style={pill(RGB.amber)}>{t('ext.paused', 'Paused')}: {match.pause.type}</span>}
          {sb && sb.bomb && <span style={pill(SIDE.T.rgb)}>{t('ext.bombPlanted', 'Bomb planted {{site}}', { site: sb.bomb.site || '' })}</span>}
          <span style={pill(RGB.gray)}>BO{match.best_of}</span>
          {(match.match_point || match.map_point) && (
            <span style={pill(match.match_point ? RGB.red : RGB.amber)}>
              {match.match_point
                ? t('ext.feed.matchPoint', 'Match point: {{team}}', { team: match.teams[match.match_point].name })
                : t('ext.feed.mapPoint', 'Map point: {{team}}', { team: match.teams[match.map_point].name })}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <span style={{ ...pill(conn === 'live' ? RGB.green : RGB.amber) }}>{conn === 'live' ? t('ext.connLive', 'Live') : t('ext.connReconnecting', 'Reconnecting…')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', fontFamily: 'var(--font-display, sans-serif)', color: 'var(--text-primary)' }}>{s1.name}</div>
            {side1 && <span style={pill(SIDE[side1].rgb)}>{side1}</span>}
          </div>
          {bigScore(score.team1, side1)}
          <div style={{ textAlign: 'center' }}>
            <div style={{ ...sectionLabel }}>{sb && sb.map ? sb.map.replace(/^de_/, '') : ''}</div>
            {match.best_of > 1 && <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{match.series.team1} – {match.series.team2}</div>}
          </div>
          {bigScore(score.team2, side2)}
          <div style={{ flex: '1 1 200px' }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', fontFamily: 'var(--font-display, sans-serif)', color: 'var(--text-primary)' }}>{s2.name}</div>
            {side2 && <span style={pill(SIDE[side2].rgb)}>{side2}</span>}
          </div>
        </div>
        {match.maps && match.maps.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.9rem' }}>
            {match.maps.map(mp => (
              <span key={mp.index} style={pill(mp.forfeit ? RGB.amber : mp.status === 'live' ? RGB.green : mp.status === 'finished' ? RGB.gray : RGB.blue)}>
                {(mp.name || '?').replace(/^de_/, '')}{mp.forfeit ? ` · ${t('ext.mapForfeitShort', 'forfeit')}` : mp.score ? ` ${mp.score.team1}-${mp.score.team2}` : ''}
              </span>
            ))}
          </div>
        )}
        {match.sources.length === 0 && match.status !== 'finished' && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.76rem', color: 'var(--accent-amber)', textAlign: 'center' }}>{t('ext.noSourceAttached', 'No game server is attached to this match yet. It is attached automatically when its logs show these team names, or from the tournament page.')}</div>
        )}
      </div>

      {sb && (
        <>
          <TeamTable team={s1} players={sb.teams.team1} side={side1} roundLive={sb.round_live} />
          <TeamTable team={s2} players={sb.teams.team2} side={side2} roundLive={sb.round_live} />
        </>
      )}
      {match.veto && <ExtVetoSummary veto={match.veto} />}
      <RoundStrip rounds={rounds} mapIndex={mapIndex} />
      <ExtPlayerStats tournamentId={tid} matchId={mid} match={match} refreshKey={rounds.length} extraCols={FLUX3_COLS} />
      <ExtMatchFeed events={events} match={match} />

      {match.status !== 'cancelled' && <ExtMapLogs tournamentId={tid} matchId={mid} teams={match.teams} onChanged={loadRest} />}

      {/* Admin tools */}
      <div style={card}>
        <h2 style={h2}>{t('ext.matchTools', 'Match tools')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <button type="button" style={btn(RGB.blue)} onClick={() => setShowRoster(!showRoster)}><Icon d={ICONS.edit} /> {t('ext.editRoster', 'Roster')}</button>
          <a style={{ ...btn(RGB.gray), textDecoration: 'none' }} href={`/api/v1/ext/tournaments/${tid}/matches/${mid}/raw-logs?format=text&download=1&token=${encodeURIComponent(localStorage.getItem('token') || '')}`}><Icon d={ICONS.download} /> {t('ext.downloadLogs', 'Download raw logs')}</a>
          <ConfirmButton label={t('ext.rebuild', 'Re-parse from logs')} icon={ICONS.refresh} color={RGB.amber} warning={t('ext.rebuildWarning', 'Rebuilds all data from the stored logs; event ids change')}
            onConfirm={async () => { await axios.post(`/api/v1/ext/tournaments/${tid}/matches/${mid}/rebuild`); loadRest(); }} />
          {match.status !== 'cancelled' && <ConfirmButton label={t('ext.cancelMatch', 'Cancel match')} icon={ICONS.trash} warning={t('ext.cancelMatchWarning', 'Detaches its servers; stored logs are kept')}
            onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${tid}/matches/${mid}`); navigate(`/admin/tournaments/${tid}`); }} />}
          <ConfirmButton label={t('common.delete', 'Delete')} icon={ICONS.trash}
            warning={t('ext.deleteMatchWarning', 'Deletes the match and its stats for good. Its servers are detached; raw logs are kept.')}
            onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${tid}/matches/${mid}`, { params: { hard: 1 } }); navigate(`/admin/tournaments/${tid}`); }} />
        </div>
      </div>
      {showRoster && <RosterEditor match={match} onSaved={() => { setShowRoster(false); loadRest(); }} />}
    </div>
  );
}
